import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/* Éditeur réutilisable : gate d'entrée (§1.2a) + profil de sortie TP/SL (§1.2b)
   d'une entité armée (wallet / creator / rule).
   - `initial` : valeurs courantes (null/undefined = champ vide = constante tp_watcher).
   - `onSave(values)` : promesse ; values = {max_pct_above_genesis, max_progress,
       max_token_age_sec, crash_brutal_velocity_threshold, crash_brutal_grace_sec,
       tp_take_pct, trailing_pct} (chaque champ number|null).
   - `showAge` : afficher max_token_age_sec (curve/rule seulement, masqué pour wallet).
   - `kind` + `address` : activent le bouton « Recommander les paramètres »
     (wallet|creator uniquement ; rule = désactivé V1).
   Styles inline (composant utilisé dans 2 scopes CSS différents : .tdp-* et .tdp-overlay-*). */

const GATE_FIELDS = [
  { k: 'max_pct_above_genesis', ph: 'max % au-dessus du launch (ex: 0.40)', age: false },
  { k: 'max_progress', ph: 'max progress courbe (ex: 0.15)', age: false },
  { k: 'max_token_age_sec', ph: 'max âge token (s) — curve/rule', age: true },
];
const SELL_FIELDS = [
  { k: 'crash_brutal_velocity_threshold', ph: 'CRASH_BRUTAL vélocité (%/s, ex: -0.70)' },
  { k: 'crash_brutal_grace_sec', ph: 'grâce CRASH_BRUTAL (s, ex: 1.5)' },
  { k: 'tp_take_pct', ph: 'TP take, en % de PnL (ex: 30 = vendre à +30%)' },
  { k: 'trailing_pct', ph: 'trailing, FRACTION du pic (ex: 0.10 = -10% depuis le pic)' },
];

const ALL_KEYS = [...GATE_FIELDS, ...SELL_FIELDS].map(f => f.k);

// Résumé compact (replié) : champs réellement renseignés → pills. Labels courts.
const SUMMARY_LABELS = [
  ['max_pct_above_genesis', (v) => `gen ≤${v}`],
  ['max_progress', (v) => `prog ≤${v}`],
  ['max_token_age_sec', (v) => `âge ≤${v}s`],
  ['crash_brutal_velocity_threshold', (v) => `CB ${v}`],
  ['crash_brutal_grace_sec', (v) => `grâce ${v}s`],
  ['tp_take_pct', (v) => `TP ${v}`],
  ['trailing_pct', (v) => `trail ${v}`],
];

const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const toStr = (v) => (v === null || v === undefined ? '' : String(v));

const S = {
  wrap: { marginTop: 14, marginBottom: 18, padding: 12, border: '1px solid var(--border,#1f2329)', borderRadius: 10, background: 'var(--surface2,#191c23)' },
  hd: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.04em', color: 'var(--text,#e6e8ec)', marginBottom: 10, cursor: 'pointer', userSelect: 'none' },
  pill: { fontSize: 9.5, fontWeight: 600, letterSpacing: 0, color: 'var(--cyan,#22d3ee)', background: 'rgba(34,211,238,.1)', border: '1px solid rgba(34,211,238,.25)', borderRadius: 5, padding: '1px 6px' },
  mutedTag: { fontSize: 10, fontWeight: 400, letterSpacing: 0, color: 'var(--text2,#8a8f98)' },
  grp: { fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text2,#8a8f98)', fontWeight: 600, margin: '6px 0 4px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },
  in: { background: 'var(--surface,#14161c)', border: '1px solid var(--border,#1f2329)', borderRadius: 6, color: 'var(--text,#e6e8ec)', fontFamily: 'inherit', fontSize: 11, padding: '6px 8px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  bar: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' },
  save: { background: 'var(--cyan,#22d3ee)', border: 'none', color: '#06121a', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, padding: '7px 14px', borderRadius: 7, cursor: 'pointer' },
  msg: { fontSize: 10.5 },
  recBtn: { background: 'none', border: '1px solid var(--amber,#ffb020)', color: 'var(--amber,#ffb020)', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, padding: '7px 14px', borderRadius: 7, cursor: 'pointer' },
  recBanner: { marginTop: 8, fontSize: 10, color: 'var(--text2,#8a8f98)', padding: '6px 8px', background: 'var(--surface,#14161c)', borderRadius: 6, border: '1px solid var(--border,#1f2329)' },
  recDone: { marginTop: 8, padding: '8px 10px', background: 'rgba(34,211,238,.06)', borderRadius: 7, border: '1px solid rgba(34,211,238,.2)', fontSize: 10.5 },
  applyBtn: { background: 'var(--amber,#ffb020)', border: 'none', color: '#1a1200', fontFamily: 'inherit', fontSize: 10.5, fontWeight: 700, padding: '5px 12px', borderRadius: 6, cursor: 'pointer' },
};

export default function EntityConfigEditor({ initial = {}, onSave, showAge = true, defaultOpen = false, label = 'Config entrée + sortie (TP/SL)', kind, address }) {
  const [open, setOpen] = useState(defaultOpen);
  const [form, setForm] = useState(() => Object.fromEntries(ALL_KEYS.map(k => [k, toStr(initial[k])])));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Recommendation state
  const [recState, setRecState] = useState(null); // null|'loading'|'polling'|'data-insuffisante'|'done'|'error'
  const [recData, setRecData] = useState(null);
  const [recErr, setRecErr] = useState(null);
  const [recApplied, setRecApplied] = useState(false); // prefill poussé dans le formulaire ?
  const shouldPollRef = useRef(false);
  const pollTimerRef = useRef(null);

  // Re-prefill quand l'entité change (panel rouvert sur une autre adresse).
  useEffect(() => {
    setForm(Object.fromEntries(ALL_KEYS.map(k => [k, toStr(initial[k])])));
    setMsg(null);
    // Reset rec state on entity change
    shouldPollRef.current = false;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setRecState(null); setRecData(null); setRecErr(null); setRecApplied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.__key]);

  // Cleanup on unmount
  useEffect(() => () => {
    shouldPollRef.current = false;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  const doFetch = useCallback(async () => {
    try {
      const resp = await api.get(`/arming/recommend/${kind}/${encodeURIComponent(address)}`);
      const d = resp.data;
      if (d.backtest_status === 'pending' || d.backtest_status === 'running') {
        setRecData(d);
        setRecState('polling');
        if (shouldPollRef.current) {
          pollTimerRef.current = setTimeout(doFetch, 4000);
        }
        return;
      }
      shouldPollRef.current = false;
      setRecData(d);
      if (d.verdict === 'DATA INSUFFISANTE' || !d.prefill) {
        setRecState('data-insuffisante');
        return;
      }
      // N'écrit PAS dans le formulaire ici : la reco s'affiche, l'utilisateur
      // l'applique explicitement via le bouton « Appliquer » (puis Enregistrer).
      setRecApplied(false);
      setRecState('done');
    } catch (e) {
      shouldPollRef.current = false;
      setRecErr(e?.response?.data?.detail || e?.message || 'inconnue');
      setRecState('error');
    }
  }, [kind, address]);

  const recommend = () => {
    if (!kind || !address || kind === 'rule') return;
    shouldPollRef.current = true;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setRecState('loading');
    setRecData(null); setRecErr(null); setRecApplied(false);
    doFetch();
  };

  // Pousse le prefill de la reco dans le formulaire (champs absents → intacts).
  const applyPrefill = () => {
    const p = recData?.prefill;
    if (!p) return;
    const mapped = {};
    if (p.max_pct_above_genesis != null) mapped.max_pct_above_genesis = toStr(p.max_pct_above_genesis);
    if (p.tp_take_pct != null) mapped.tp_take_pct = toStr(p.tp_take_pct);
    setForm(f => ({ ...f, ...mapped }));
    setRecApplied(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const values = Object.fromEntries(ALL_KEYS.map(k => [k, num(form[k])]));
      await onSave(values);
      setMsg({ ok: true, t: 'Enregistré ✓ — pris en compte ≤1,2 s (snapshot live).' });
    } catch (e) {
      setMsg({ ok: false, t: `Erreur : ${e?.response?.data?.detail || e?.message || 'inconnue'}` });
    } finally {
      setSaving(false);
    }
  };

  const gateFields = GATE_FIELDS.filter(f => showAge || !f.age);

  // Replié : mettre en avant les seuls champs renseignés (overrides), sinon « défauts du bot ».
  const summaryPills = SUMMARY_LABELS
    .filter(([k]) => showAge || k !== 'max_token_age_sec')
    .filter(([k]) => form[k] !== '' && form[k] != null)
    .map(([k, fn]) => fn(form[k]));

  // Recommendation computed values (stable, derived from recData)
  const recBusy = recState === 'loading' || recState === 'polling';
  const recConf = recData?.confidence || {};
  const recEntryRec = recData?.entry?.recommended_max_pct_above_genesis;
  const recExitRec = recData?.exit?.recommended_tp_take_pct;
  const recBestGrid = Array.isArray(recData?.entry?.grid) && recData?.entry?.shrunk_from != null
    ? recData.entry.grid.find(g => Math.abs(g.t - recData.entry.shrunk_from) < 0.001) || null
    : null;
  const recPrefill = recData?.prefill || null;
  const recHasPrefill = !!recPrefill && (recPrefill.max_pct_above_genesis != null || recPrefill.tp_take_pct != null);

  const showRecBtn = kind && address;
  const recBtnDisabled = recBusy || kind === 'rule';
  const recBtnLabel = recState === 'loading'
    ? 'Calcul des recommandations…'
    : recState === 'polling'
      ? 'Backtest cohorte en cours…'
      : 'Recommander les paramètres';

  return (
    <div style={S.wrap}>
      <div style={S.hd} onClick={() => setOpen(o => !o)}>
        <span>{open ? '▾' : '▸'}</span> ⚙ {label}
        {!open && (summaryPills.length
          ? summaryPills.map((p, i) => <span key={i} style={S.pill}>{p}</span>)
          : <span style={S.mutedTag}>défauts du bot</span>)}
      </div>
      {open && (
        <>
          <div style={S.grp}>Gate d'entrée — vide = aucune contrainte</div>
          <div style={S.row}>
            {gateFields.map(f => (
              <input key={f.k} style={S.in} type="number" step="any" placeholder={f.ph}
                value={form[f.k]} onChange={e => set(f.k, e.target.value)} />
            ))}
          </div>
          <div style={S.grp}>Profil de sortie — vide = constantes par défaut du bot</div>
          <div style={S.row}>
            {SELL_FIELDS.map(f => (
              <input key={f.k} style={S.in} type="number" step="any" placeholder={f.ph}
                value={form[f.k]} onChange={e => set(f.k, e.target.value)} />
            ))}
          </div>
          <div style={S.bar}>
            <button style={{ ...S.save, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            {showRecBtn && (
              <button
                style={{ ...S.recBtn, opacity: recBtnDisabled ? 0.5 : 1, cursor: recBtnDisabled ? 'not-allowed' : 'pointer' }}
                disabled={recBtnDisabled}
                title={kind === 'rule' ? 'non supporté V1' : undefined}
                onClick={recommend}
              >
                {recBtnLabel}
              </button>
            )}
            {msg && <span style={{ ...S.msg, color: msg.ok ? 'var(--green,#00e08a)' : 'var(--red,#ff3b5c)' }}>{msg.t}</span>}
          </div>
          {/* Recommendation results — displayed below the action bar */}
          {recState === 'polling' && recData && (
            <div style={S.recBanner}>
              {`Backtest cohorte en cours (${recData.backtest_status})…`}
            </div>
          )}
          {recState === 'data-insuffisante' && (
            <div style={S.recBanner}>
              {`Pas assez de données (n_dec=${recData?.n_dec ?? 0} < 8) — réglage manuel.`}
            </div>
          )}
          {recState === 'done' && recData && (
            <div style={S.recDone}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
                <span style={S.pill}>{recData.verdict}</span>
                {recEntryRec != null && <span style={S.pill}>{`gen ≤${recEntryRec}`}</span>}
                {recData?.entry?.oos_veto && (
                  <span style={S.mutedTag} title="Recommandation gate retenue hors prefill : échec validation out-of-sample">
                    {`gate veto OOS (${recData.entry.oos_veto})`}
                  </span>
                )}
                {recExitRec != null && <span style={S.pill}>{`TP ${recExitRec}`}</span>}
                {recData?.exit?.oos_veto && (
                  <span style={S.mutedTag} title="Recommandation TP retenue hors prefill : échec validation out-of-sample">
                    {`TP veto OOS (${recData.exit.oos_veto})`}
                  </span>
                )}
                {recConf.n_dec != null && <span style={S.mutedTag}>{`n_dec=${recConf.n_dec}`}</span>}
                {recConf.n_headroom != null && <span style={S.mutedTag}>{`headroom n=${recConf.n_headroom}`}</span>}
                {recBestGrid != null && recBestGrid.test_ret != null && (
                  <span style={S.mutedTag}>
                    {`OOS test ${recBestGrid.test_ret >= 0 ? '+' : ''}${Number(recBestGrid.test_ret).toFixed(1)}%`}
                  </span>
                )}
              </div>
              {recHasPrefill ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                  <button style={S.applyBtn} onClick={applyPrefill}>
                    {recApplied ? 'Ré-appliquer' : 'Appliquer au formulaire'}
                  </button>
                  {recApplied
                    ? <span style={{ fontSize: 10, color: 'var(--green,#00e08a)' }}>Appliqué ✓ — relisez puis Enregistrer.</span>
                    : <span style={{ fontSize: 10, color: 'var(--amber,#ffb020)' }}>Relisez, appliquez, puis Enregistrer.</span>}
                </div>
              ) : (
                <div style={{ fontSize: 10, color: 'var(--text2,#8a8f98)' }}>Aucun paramètre à appliquer (verdict informatif).</div>
              )}
            </div>
          )}
          {recState === 'error' && (
            <div style={{ ...S.msg, color: 'var(--red,#ff3b5c)', marginTop: 6 }}>
              {`Erreur reco : ${recErr}`}
            </div>
          )}
        </>
      )}
    </div>
  );
}
